import React from "react";
import { createUseStyles } from "react-jss";
import { Grid, IconButton, Tooltip, Select, MenuItem } from "@material-ui/core";
import HeaderLogo from "./HeaderLogo";
import { useTranslation } from "react-i18next";
import NavigationTabs from "./NavigationTabs";
import { useDispatch, useSelector } from "react-redux";
import { loadPorfolioInformation } from "redux/portfolioInformation/portfolioInformationFunctions";
import { RootState } from "redux/rootReducer";
import { asEur, asPercent } from "utility/numbers";
import { loadYieldPerTimespans } from "redux/yieldPerTimespan/yieldPerTimespanFunctions";
import { loadUserPermissions } from "redux/user/userFunctions";
import { selectUserPermission } from "redux/user/userActions";
import { getReportDateSelector } from "redux/user/user";
import { UserPermission } from "types/UserPermission";
import { removeState } from "redux/oidc/oidcFunctions";
import { useHistory } from "react-router-dom";
import Background from "img/Background.png";
import { dateToString } from "../utility/date";
import { VisibleSections } from "types/VisibleSections";
import { loadCovenantsCount } from "redux/covenants/covenantsFunctions";

const useStyles = createUseStyles({
  container: {
    minHeight: 90,
    width: "100%",
    position: "static",
    backgroundImage: `url(${Background})`,
  },
  headerBar: {
    minHeight: 58,
  },
  logo: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  mainInfo: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  centerItems: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  justifyItemsStart: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  justifyItemsEnd: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  line: {
    height: "calc(100% + 10px)",
    paddingLeft: 1,
    paddingRight: 1,
    marginRight: 40,
    background: "#ffffff",
  },
  whiteColor: {
    color: "#FFFFFF !important",
  },
});

const MainHeader = () => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const history = useHistory();

  const reportControlId = useSelector((state: RootState) => state.user.reportControlId);
  const reportDate = useSelector(getReportDateSelector);

  const showIndex = useSelector(
    (state: RootState) =>
      (state.user.visibleSections?.data as VisibleSections | undefined)?.NaytaTuotto ?? false
  );

  React.useEffect(() => {
    dispatch(loadUserPermissions());
    if (reportControlId && reportDate) {
      dispatch(loadPorfolioInformation(reportControlId, reportDate));
      dispatch(loadYieldPerTimespans(reportControlId, reportDate, showIndex));
      dispatch(loadCovenantsCount(reportControlId, reportDate));
    }
  }, [dispatch, reportControlId, reportDate, showIndex]);

  const portfolioInformation = useSelector((state: RootState) => state.portfolioInformation.data);

  const userPermissions = useSelector(
    (state: RootState) => state.user.userPermissions?.data as UserPermission[] | undefined
  );

  const userPermissionsSorted = React.useMemo(
    () => userPermissions?.sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? "")),
    [userPermissions]
  );

  const visibleSections = useSelector(
    (state: RootState) => state.user.visibleSections?.data as VisibleSections | undefined
  );

  return (
    <div className={classes.container}>
      <Grid container className={classes.headerBar} justifyContent="center">
        <Grid item container md={10} sm={12}>
          <Grid item xl={2} xs={3} className={classes.logo}>
            <HeaderLogo smallLogo />
            <h3
              style={{
                color: "#FFFFFF",
                margin: "auto",
                fontSize: "min(1.2em, 1.5vw)",
                letterSpacing: "0",
              }}
            >
              {t("property_report")}
            </h3>
          </Grid>
          <Grid container item xl={10} xs={9} className={classes.mainInfo}>
            <Grid item xs={3} className={classes.justifyItemsStart}>
              <div className={classes.line} />
              <Select
                classes={{
                  icon: classes.whiteColor,
                }}
                style={{ color: "#FFFFFF" }}
                disableUnderline
                inputProps={{ style: { color: "#FFFFFF" } }}
                value={reportControlId ?? ""}
                onChange={(event) => {
                  dispatch(selectUserPermission(event.target.value as string));
                  history.push("/");
                }}
              >
                {userPermissionsSorted?.map((p) => (
                  <MenuItem key={p.ReportControlId} value={p.ReportControlId}>
                    {p.Name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={2} className={classes.justifyItemsEnd}>
              <p style={{ color: "#FFFFFF", fontFamily: "Lato light" }}>
                {t("portfolio_market_value")}:<br></br>
                <span style={{ fontSize: "1.4em" }}>
                  {asEur(portfolioInformation?.MarkkinaArvo)}
                </span>
              </p>
            </Grid>
            <Grid item xs={2} className={classes.justifyItemsEnd} style={{ paddingRight: 8 }}>
              <p style={{ color: "#FFFFFF", fontFamily: "Lato light" }}>
                {t("profit_from_start")}
                <br></br>
                <span style={{ fontSize: "1.4em" }}>
                  {asPercent(portfolioInformation?.TuottoAlusta)}
                </span>
              </p>
            </Grid>
            {Boolean(visibleSections?.NaytaESG) && (
              <Grid item xs={2} className={classes.justifyItemsEnd}>
                <p style={{ color: "#FFFFFF", fontFamily: "Lato light" }}>
                  {t("impact_esg")}:<br></br>
                  <span style={{ fontSize: "1.4em" }}>
                    {asPercent(portfolioInformation?.NetImpactRatio, 0)}
                  </span>
                </p>
              </Grid>
            )}
            <Grid item xs={2} className={classes.justifyItemsEnd}>
              <p style={{ color: "#FFFFFF", fontFamily: "Lato light" }}>
                {t("portfolio_start")}:<br></br>
                <span style={{ fontSize: "1.4em" }}>
                  {portfolioInformation !== undefined
                    ? dateToString(portfolioInformation?.SalkkuAloitettu, "D.M.YYYY")
                    : "-"}
                </span>
              </p>
            </Grid>
            <Grid item xs={1} className={classes.justifyItemsEnd}>
              <Tooltip title={t<string>("logout")} aria-label={t("logout")}>
                <IconButton
                  onClick={() => {
                    dispatch(removeState());
                    history.push("/logout");
                  }}
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 36 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 7.55932L17 4V34L9 29.9322V7.55932Z"
                      stroke="#FFFFFF"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 7.5L23 7.5V15.5M17 29.5H23V22.5"
                      stroke="#FFFFFF"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <ellipse cx="14.5" cy="18.5" rx="0.5" ry="1" fill="#FFFFFF" />
                    <path
                      d="M20 18.5H30M30 18.5L27 15.5M30 18.5L27 21.5"
                      stroke="#FFFFFF"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container xs={12} justifyContent="center">
        <Grid item md={10} sm={12} style={{ overflowY: "hidden" }}>
          <NavigationTabs />
        </Grid>
      </Grid>
    </div>
  );
};

export default React.memo(MainHeader);
